import React, { useEffect } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import { getService } from '@Client/runner.hooks/useService';
import IFlowModelTag from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/flowModelTag';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import CustomTabs, {
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';

export type Props = {
  title: string;
  flowmodelId?: Guid;
  procedureId?: Guid;
  onClose?: () => void;
};

const LibraryTagsModal = ({
  title,
  flowmodelId,
  procedureId,
  onClose
}: Props) => {
  const [flowModelTags, setFlowModelTags] = React.useState<IFlowModelTag>({
    flowModelName: '',
    tags: [],
    stepTags: []
  });
  const [procedureTags, setProcedureTags] = React.useState<ITagGroupDetail[]>(
    []
  );

  const flowModelApiService = getService('flowModelApiService');
  const procedureApiService = getService('procedureApiService');
  const [selectedTab, setSelectedTab] = React.useState<TabKeys>(
    flowmodelId ? TabKeys.FlowModelTags : TabKeys.ProcedureTags
  );

  const tabs: ICustomTab[] = [
    {
      key: TabKeys.FlowModelTags,
      label: 'Flow Model Tags',
      isHidden: !flowmodelId
    },
    { key: TabKeys.StepTags, label: 'Step Tags', isHidden: !flowmodelId },
    {
      key: TabKeys.ProcedureTags,
      label: 'Procedure Tags',
      isHidden: !procedureId
    }
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    getTags();
  }, []);

  const sortTagsByName = (tagGroups: ITagGroupDetail[]) => {
    tagGroups.forEach((group) => {
      if (group.tags) {
        group.tags.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return tagGroups;
  };

  const getTags = async () => {
    if (flowmodelId) {
      const response = await flowModelApiService.getTags(flowmodelId);
      sortTagsByName(response.tags || []);
      sortTagsByName(response.stepTags || []);
      setFlowModelTags(response);
    } else if (procedureId) {
      const procedureTagGroups = await procedureApiService.getProcedureTags(
        procedureId
      );
      sortTagsByName(procedureTagGroups);
      setProcedureTags(procedureTagGroups);
    }
  };

  const tagGroupsFormatted = (tagGroups: ITagGroupDetail[]) => {
    const result: Record<string, { tags: string[]; color: string }> = {};
    tagGroups.forEach((group) => {
      result[group.name] = {
        tags: group.tags.map((tag) => tag.name),
        color: group.color
      };
    });
    return result;
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      onCancelClick={onClose}
      hideActionButton={true}
      cancelButtonText={'Close'}
      size="lg"
    >
      <div className="library-tag-container">
        <div className="library-tag-tabs">
          <CustomTabs
            tabs={tabs}
            selectedTabIndex={selectedTab}
            onChange={handleChange}
          ></CustomTabs>
        </div>

        <div className="library-tag-content">
          {selectedTab === TabKeys.FlowModelTags && (
            <div>
              <TagList
                groupedSelectedTags={tagGroupsFormatted(flowModelTags.tags)}
              />
            </div>
          )}
          {selectedTab === TabKeys.StepTags && (
            <div>
              {' '}
              <TagList
                groupedSelectedTags={tagGroupsFormatted(flowModelTags.stepTags)}
              />
            </div>
          )}
          {selectedTab === TabKeys.ProcedureTags && (
            <div>
              {' '}
              <TagList
                groupedSelectedTags={tagGroupsFormatted(procedureTags)}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LibraryTagsModal;

enum TabKeys {
  FlowModelTags = 0,
  StepTags = 1,
  ProcedureTags = 2
}
